import { Fragment } from 'react';
import { ChevronRight, Mail } from 'lucide-react';
import RewardCoin from '@/components/Icons/RewardCoin';
import { cn } from '@/lib/utils';
import { AvatarImage, AvatarFallback, Avatar } from '@/components/ui/avatar';
import { TIER_HEX } from '@/lib/points';
import { IUser } from '@/redux/auth/auth';
import { useGetMyPointsSummaryQuery } from '@/redux/points/points.api.slice';
import EditProfileModal from '@/redux/auth/EditProfile/EditProfileModal';
import FollowButton from './FollowButton';
import FollowersModal, { STAT_CELL, StatValue } from './FollowersModal';
import ProfileLinks from './ProfileLinks';
import TierRoad, { nextTierText, tierLabel } from './PointsSection/TierRoad';
import { useProfileAchievements } from './useProfileAchievements';
import { RewardChips, Stamp } from './AchievementParts';
import RewardsWallet from './PointsSection';
import ProfileDialog from './ProfileDialog';
import Achievements from './Achievements';
import MachinesSection from './MachinesSection';

type AchievementsData = NonNullable<ReturnType<typeof useProfileAchievements>>;

interface LevelCardProps {
  user: IUser;
  isOwnProfile: boolean;
  isAuthenticated: boolean;
}

function ProfileAvatar({ user, ring }: { user: IUser; ring?: string }) {
  return (
    <Avatar
      className="h-24 w-24 shrink-0 border-4 border-card shadow-md sm:h-28 sm:w-28"
      style={ring ? { borderColor: ring } : undefined}
    >
      <AvatarImage
        src={user.profilePicture}
        alt=""
        className="h-full w-full object-cover"
      />
      <AvatarFallback className="bg-green-100 font-display text-3xl text-green-800">
        {user.username.charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

const shownIf = (visible: boolean | undefined, value: string | undefined) =>
  visible === false ? '' : (value ?? '');

const nameParts = (user: IUser) =>
  [
    shownIf(user.privacy?.firstname, user.firstname),
    shownIf(user.privacy?.lastname, user.lastname),
  ].filter(Boolean);

function DisplayName({ user }: { user: IUser }) {
  const parts = nameParts(user);
  return (
    <h1 className="break-words font-display text-2xl font-semibold leading-tight tracking-[-0.02em] text-green-950 sm:text-3xl">
      {parts.length === 0
        ? `@${user.username}`
        : parts.map((part) => (
            <Fragment key={part}>
              <span>{part}</span>{' '}
            </Fragment>
          ))}
    </h1>
  );
}

const countOf = (value: number | undefined) => value ?? 0;

function ProfileStats({
  user,
  isOwnProfile,
}: {
  user: IUser;
  isOwnProfile: boolean;
}) {
  return (
    <div className="flex w-full divide-x divide-green-100 overflow-hidden rounded-lg border border-green-200 bg-card sm:w-fit">
      <div className="flex-1">
        <a href="#profile-projects" className={STAT_CELL}>
          <StatValue count={countOf(user.fileCount)} label="Posts" />
        </a>
      </div>
      <FollowersModal
        targetUserId={user._id}
        mode="followers"
        count={countOf(user.followersCount)}
      />
      <FollowersModal
        targetUserId={user._id}
        mode="following"
        count={countOf(user.followingCount)}
      />
      <div className="flex-1">
        <ProfileDialog
          title="CNC Machines"
          description="CNC machines this maker owns"
          trigger={
            <button type="button" className={STAT_CELL}>
              <StatValue
                count={countOf(user.ownedMachines?.length)}
                label="Machines"
              />
            </button>
          }
        >
          <MachinesSection user={user} isOwnProfile={isOwnProfile} />
        </ProfileDialog>
      </div>
    </div>
  );
}

const linksVisible = (privacy: IUser['privacy']) =>
  privacy ? (privacy.links ?? privacy.website ?? true) : true;

function EmailLine({ user }: { user: IUser }) {
  const email = shownIf(user.privacy?.email, user.email);
  if (!email) return null;
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5 text-sm text-green-900/70">
      <Mail className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{email}</span>
    </span>
  );
}

function ProfileActions({
  user,
  isOwnProfile,
  isAuthenticated,
}: LevelCardProps) {
  return (
    <div className="mt-1 flex flex-wrap gap-2 [&>*]:w-auto [&_button]:w-auto [&_button]:px-5">
      <EditProfileModal user={user} />
      {isAuthenticated && (
        <FollowButton
          targetUserId={user._id}
          isFollowing={Boolean(user.isFollowing)}
          isOwnProfile={isOwnProfile}
        />
      )}
    </div>
  );
}

function Identity(props: LevelCardProps) {
  const { user, isOwnProfile } = props;
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div>
        <DisplayName user={user} />
        <p className="text-sm font-medium text-green-800">@{user.username}</p>
      </div>
      <ProfileStats user={user} isOwnProfile={isOwnProfile} />
      <EmailLine user={user} />
      {linksVisible(user.privacy) && <ProfileLinks user={user} />}
      <ProfileActions {...props} />
    </div>
  );
}

const UP_NEXT_COUNT = 2;
const TOP_COUNT = 6;

function TopAchievements({
  data,
  wide,
}: {
  data: AchievementsData;
  wide: boolean;
}) {
  const top = data.catalog
    .filter((entry) => data.earned.has(entry.id))
    .sort((a, b) => b.points - a.points)
    .slice(0, TOP_COUNT);
  if (top.length === 0) return null;
  return (
    <ul
      className={cn('mt-5 grid grid-cols-3 gap-2', wide && '2xl:grid-cols-6')}
      aria-label="Toughest achievements earned"
      data-testid="achievements-top"
    >
      {top.map((entry) => (
        <li
          key={entry.id}
          title={entry.description}
          className="flex flex-col items-center gap-1.5 rounded-lg border border-green-100 bg-card px-2 py-3 text-center"
        >
          <Stamp entry={entry} state="earned" />
          <p className="line-clamp-2 text-xs font-medium leading-tight text-green-950">
            {entry.name}
          </p>
          <p className="text-[11px] font-semibold tabular-nums text-green-800">
            {entry.points.toLocaleString()} XP
          </p>
        </li>
      ))}
    </ul>
  );
}

function SeeMoreAchievements({
  username,
  isOwnProfile,
}: {
  username: string;
  isOwnProfile: boolean;
}) {
  return (
    <ProfileDialog
      title="Achievements"
      fixedHeight
      description="Every achievement and what it pays"
      trigger={
        <button
          type="button"
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-green-300 px-3 py-3 text-sm font-medium text-green-800 transition-colors hover:border-green-400 hover:bg-green-100 focus-visible:outline-none focus-visible:shadow-focus"
        >
          See more achievements
          <ChevronRight className="h-4 w-4" />
        </button>
      }
    >
      <Achievements
        username={username}
        isOwnProfile={isOwnProfile}
        initialShowAll={isOwnProfile}
      />
    </ProfileDialog>
  );
}

function UpNext({ data }: { data: AchievementsData }) {
  const next = data.catalog
    .filter((entry) => entry.available && !data.earned.has(entry.id))
    .slice(0, UP_NEXT_COUNT);
  if (next.length === 0) return null;
  return (
    <div className="mt-5" data-testid="achievements-up-next">
      <h3 className="mb-2 text-sm font-semibold text-green-950">Up next</h3>
      <ol className="flex flex-col gap-2">
        {next.map((entry) => (
          <li
            key={entry.id}
            className="flex items-center gap-3 rounded-lg border border-green-100 bg-card px-3 py-2.5"
          >
            <Stamp entry={entry} state="locked" size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{entry.name}</p>
              <p className="truncate text-xs text-muted-foreground">
                {entry.description}
              </p>
            </div>
            <RewardChips entry={entry} />
          </li>
        ))}
      </ol>
    </div>
  );
}

function XpMeter({
  username,
  isOwnProfile,
}: {
  username: string;
  isOwnProfile: boolean;
}) {
  const data = useProfileAchievements(username);
  if (!data) return <div className="h-[104px]" />;
  const label = `${data.earned.size} of ${data.catalog.length} achievements earned`;
  const pct = Math.round((data.earned.size / data.catalog.length) * 100);
  return (
    <div data-testid="profile-xp">
      <p className="font-display text-5xl font-semibold leading-none tracking-[-0.03em] tabular-nums text-green-900 sm:text-6xl">
        {data.totalPoints.toLocaleString()}
        <span className="ml-1.5 text-xl font-medium tracking-normal text-green-700">
          XP
        </span>
      </p>
      <div className="mt-5 flex items-baseline justify-between text-sm tabular-nums text-green-900/70">
        <span>
          <span className="font-semibold text-green-900">
            {data.earned.size} of {data.catalog.length}
          </span>{' '}
          achievements
        </span>
        <span className="font-semibold text-green-800">{pct}%</span>
      </div>
      <div
        className="mt-2 h-2.5 overflow-hidden rounded-full bg-green-200/60"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={data.catalog.length}
        aria-valuenow={data.earned.size}
      >
        <div
          className="xp-fill h-full w-full origin-left rounded-full bg-green-600"
          style={{ transform: `scaleX(${pct / 100})` }}
        />
      </div>
      <TopAchievements data={data} wide={!isOwnProfile} />
      {isOwnProfile && <UpNext data={data} />}
      <SeeMoreAchievements username={username} isOwnProfile={isOwnProfile} />
    </div>
  );
}

function CoinsPanel({ user }: { user: IUser }) {
  const { data } = useGetMyPointsSummaryQuery();
  if (!data) return null;
  const tier = data.highestTier ?? data.tier;
  return (
    <div data-testid="profile-standing">
      <div className="flex items-center gap-3">
        <RewardCoin coinType={tier} width={44} height={44} />
        <p className="font-display text-5xl font-semibold leading-none tracking-[-0.03em] tabular-nums text-green-900 sm:text-6xl">
          {data.currentPoints.toLocaleString()}
          <span className="ml-1.5 text-xl font-medium tracking-normal text-green-700">
            pts
          </span>
        </p>
      </div>
      <p className="mt-5 text-sm text-green-900/70">
        Worth{' '}
        <span className="font-semibold text-green-800">
          ${data.currentPoints.toLocaleString()}
        </span>{' '}
        in Onefinity coupons
      </p>
      <div className="mb-2 mt-5 flex items-center justify-between gap-3 text-sm">
        <p
          className="inline-flex items-center gap-1.5 rounded-pill border bg-card py-1 pl-1 pr-3 font-display text-base font-semibold text-green-950"
          style={{ borderColor: TIER_HEX[tier] }}
        >
          <RewardCoin coinType={tier} width={22} height={22} />
          {tierLabel(tier)} Creator
        </p>
        <p className="tabular-nums text-green-900/70">
          {nextTierText(tier, data.currentPoints)}
        </p>
      </div>
      <TierRoad points={data.currentPoints} tier={tier} />
      <div className="mt-4">
        <RewardsWallet user={user} />
      </div>
    </div>
  );
}

const PANEL = 'min-w-0 border-green-200/70 pt-6 first:pt-0';

const LAYOUT = {
  owner: {
    card: 'xl:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]',
    panels:
      'border-t pt-6 md:grid-cols-2 md:divide-x md:divide-green-200/70 xl:border-l xl:border-t-0 xl:pl-10 xl:pt-0 md:[&>*]:px-8 md:[&>*:first-child]:pl-0 md:[&>*:last-child]:pr-0',
  },
  visitor: {
    card: 'lg:grid-cols-[minmax(0,1fr)_minmax(320px,400px)] 2xl:grid-cols-2',
    panels: 'lg:border-l lg:pl-12',
  },
};

/** Tier is owner-only, so visitors get the plain border. */
const ringOf = (user: IUser, isOwnProfile: boolean) =>
  isOwnProfile && user.highestTier ? TIER_HEX[user.highestTier] : undefined;

/** The profile's opening statement: who this maker is and what they have earned. */
export default function LevelCard(props: LevelCardProps) {
  const { user, isOwnProfile } = props;
  const layout = LAYOUT[isOwnProfile ? 'owner' : 'visitor'];
  return (
    <section
      className={cn(
        'grid gap-8 rounded-xl border border-green-100 bg-green-50 p-5 sm:p-8 lg:gap-10',
        layout.card
      )}
      data-testid="profile-level-card"
    >
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <ProfileAvatar user={user} ring={ringOf(user, isOwnProfile)} />
        <Identity {...props} />
      </div>
      <div className={cn('grid border-green-200/70', layout.panels)}>
        <div className={PANEL}>
          <XpMeter username={user.username} isOwnProfile={isOwnProfile} />
        </div>
        {isOwnProfile && (
          <div className={cn(PANEL, 'border-t md:border-t-0 md:pt-0')}>
            <CoinsPanel user={user} />
          </div>
        )}
      </div>
    </section>
  );
}
